import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getProfile,
  updateProfile,
  uploadProfilePicture,
  getLanguages,
  addLanguages,
  getEarnings,
  getMyLearning,
} from '../../api/profile';

// ──────────────────────────────────────────────
// Async Thunks
// ──────────────────────────────────────────────

export const fetchProfile = createAsyncThunk(
  'profile/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      return await getProfile();
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch profile' });
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'profile/updateUserProfile',
  async (data, { rejectWithValue }) => {
    try {
      return await updateProfile(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update profile' });
    }
  }
);

export const uploadAvatar = createAsyncThunk(
  'profile/uploadAvatar',
  async (formData, { rejectWithValue }) => {
    try {
      return await uploadProfilePicture(formData);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to upload avatar' });
    }
  }
);

export const fetchLanguages = createAsyncThunk(
  'profile/fetchLanguages',
  async (_, { rejectWithValue }) => {
    try {
      return await getLanguages();
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch languages' });
    }
  }
);

export const saveLanguages = createAsyncThunk(
  'profile/saveLanguages',
  async (language_ids, { rejectWithValue }) => {
    try {
      return await addLanguages(language_ids);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to save languages' });
    }
  }
);

export const fetchEarnings = createAsyncThunk(
  'profile/fetchEarnings',
  async (_, { rejectWithValue }) => {
    try {
      return await getEarnings();
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch earnings' });
    }
  }
);

export const fetchMyLearning = createAsyncThunk(
  'profile/fetchMyLearning',
  async (_, { rejectWithValue }) => {
    try {
      return await getMyLearning();
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch learning data' });
    }
  }
);

// ──────────────────────────────────────────────
// Slice
// ──────────────────────────────────────────────

const profileSlice = createSlice({
  name: 'profile',
  initialState: {
    profile: null,
    languages: [],
    earnings: null,
    myLearning: [],
    loading: {
      profile: false,
      update: false,
      avatar: false,
      languages: false,
      earnings: false,
      myLearning: false,
    },
    error: {
      profile: null,
      update: null,
      avatar: null,
      languages: null,
      earnings: null,
      myLearning: null,
    },
    updateSuccess: false,
    activeTab: 'info',
  },
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    clearProfileErrors: (state) => {
      state.error = {
        profile: null, update: null, avatar: null,
        languages: null, earnings: null, myLearning: null,
      };
    },
    clearUpdateSuccess: (state) => {
      state.updateSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── fetchProfile ──
      .addCase(fetchProfile.pending, (state) => {
        state.loading.profile = true;
        state.error.profile = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading.profile = false;
        state.profile = action.payload.data || action.payload.user || null;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading.profile = false;
        state.error.profile = action.payload?.message || 'Failed to fetch profile';
      })
      // ── updateUserProfile ──
      .addCase(updateUserProfile.pending, (state) => {
        state.loading.update = true;
        state.error.update = null;
        state.updateSuccess = false;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading.update = false;
        state.updateSuccess = true;
        // Merge updated fields into profile
        if (action.payload.data) {
          state.profile = { ...state.profile, ...action.payload.data };
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading.update = false;
        state.error.update = action.payload?.message || 'Failed to update profile';
      })
      // ── uploadAvatar ──
      .addCase(uploadAvatar.pending, (state) => {
        state.loading.avatar = true;
        state.error.avatar = null;
      })
      .addCase(uploadAvatar.fulfilled, (state, action) => {
        state.loading.avatar = false;
        if (action.payload.data?.profile_picture && state.profile) {
          state.profile.profile_picture = action.payload.data.profile_picture;
        }
      })
      .addCase(uploadAvatar.rejected, (state, action) => {
        state.loading.avatar = false;
        state.error.avatar = action.payload?.message || 'Failed to upload avatar';
      })
      // ── fetchLanguages ──
      .addCase(fetchLanguages.pending, (state) => {
        state.loading.languages = true;
      })
      .addCase(fetchLanguages.fulfilled, (state, action) => {
        state.loading.languages = false;
        state.languages = action.payload.data || [];
      })
      .addCase(fetchLanguages.rejected, (state, action) => {
        state.loading.languages = false;
        state.error.languages = action.payload?.message || 'Failed to fetch languages';
      })
      // ── fetchEarnings ──
      .addCase(fetchEarnings.pending, (state) => {
        state.loading.earnings = true;
      })
      .addCase(fetchEarnings.fulfilled, (state, action) => {
        state.loading.earnings = false;
        state.earnings = action.payload.data || null;
      })
      .addCase(fetchEarnings.rejected, (state, action) => {
        state.loading.earnings = false;
        state.error.earnings = action.payload?.message || 'Failed to fetch earnings';
      })
      // ── fetchMyLearning ──
      .addCase(fetchMyLearning.pending, (state) => {
        state.loading.myLearning = true;
      })
      .addCase(fetchMyLearning.fulfilled, (state, action) => {
        state.loading.myLearning = false;
        state.myLearning = action.payload.data || [];
      })
      .addCase(fetchMyLearning.rejected, (state, action) => {
        state.loading.myLearning = false;
        state.error.myLearning = action.payload?.message || 'Failed to fetch learning data';
      });
  },
});

export const { setActiveTab, clearProfileErrors, clearUpdateSuccess } = profileSlice.actions;

export default profileSlice.reducer;
